import { z } from 'zod';
import { cents, entityBase, id, timestamp } from './common.js';

/** Persisted group-level provider references. No card details or provider keys are stored here. */
export const officeBillingAccount = entityBase.extend({
  groupId: id,
  stripeCustomerId: z.string().nullable(),
  defaultPaymentMethodId: z.string().nullable(),
  paymentMethodType: z.string().nullable(),
  verifiedAt: timestamp.nullable(),
});
export type OfficeBillingAccount = z.infer<typeof officeBillingAccount>;

export const officeBillingSetupSession = entityBase.extend({
  groupId: id,
  providerSessionId: z.string().min(1),
  status: z.enum(['open', 'complete', 'expired']),
});
export type OfficeBillingSetupSession = z.infer<typeof officeBillingSetupSession>;

export const officeBillingStatus = z.object({
  groupId: id,
  ready: z.boolean(),
  cardOnFile: z.boolean(),
  providerConfigured: z.boolean(),
  customerId: z.string().nullable(),
});
export type OfficeBillingStatus = z.infer<typeof officeBillingStatus>;

export const officeCollectionStatus = z.enum([
  'ready',
  'processing',
  'succeeded',
  'failed',
  'needs_review',
]);
export type OfficeCollectionStatus = z.infer<typeof officeCollectionStatus>;

export const officeCollection = entityBase.extend({
  groupId: id,
  earningId: id,
  totalCents: cents,
  status: officeCollectionStatus,
  providerIntentId: z.string().nullable(),
  idempotencyKey: z.string().min(1),
  lastError: z.string().nullable(),
});
export type OfficeCollection = z.infer<typeof officeCollection>;
export const officeCollectionView = officeCollection.omit({ idempotencyKey: true });
export type OfficeCollectionView = z.infer<typeof officeCollectionView>;

export const officeCollectionAttempt = entityBase.extend({
  collectionId: id,
  idempotencyKey: z.string().min(1),
  providerIntentId: z.string().nullable(),
  status: officeCollectionStatus,
  lastError: z.string().nullable(),
});
export type OfficeCollectionAttempt = z.infer<typeof officeCollectionAttempt>;

export const financeLine = z.object({
  earningId: id,
  bookingId: id,
  groupId: id,
  groupName: z.string(),
  professionalName: z.string(),
  approvedAt: timestamp,
  professionalGrossCents: cents,
  officeFeeCents: cents,
  officeTotalCents: cents,
  practiceCardReady: z.boolean(),
  collectionStatus: officeCollectionStatus.nullable(),
  payoutStatus: z.string(),
});
export type FinanceLine = z.infer<typeof financeLine>;

export const financeOverview = z.object({
  provider: z.object({ stripeConfigured: z.boolean(), workMarketConfigured: z.boolean() }),
  collections: z.array(financeLine),
  payouts: z.array(financeLine),
});
export type FinanceOverview = z.infer<typeof financeOverview>;
